/**
 * Terminabstimmung per Link (Review 26.10.2026): wie eine Doodle-Umfrage ohne Anmeldung. TWW schlägt Tage vor,
 * Förster, Spendenpartner und weitere Beteiligte antworten je Tag mit „möglich“, „bedingt möglich“ oder „nicht möglich“.
 * Teilnehmende sehen nur ihre eigenen Antworten; TWW sieht alle und dazu andere Aktionen an denselben Tagen.
 */
import { z } from 'zod';

export const POLL_ANSWERS = ['yes', 'maybe', 'no'] as const;
export type PollAnswer = (typeof POLL_ANSWERS)[number];
export const POLL_ANSWER_LABELS: Record<PollAnswer, string> = { yes: 'möglich', maybe: 'bedingt möglich', no: 'nicht möglich' };
export const POLL_ROLES = ['forester', 'partner', 'other'] as const;
export type PollRole = (typeof POLL_ROLES)[number];
export const POLL_ROLE_LABELS: Record<PollRole, string> = { forester: 'Förster', partner: 'Spendenpartner', other: 'Beteiligte' };

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const PollParticipantInputSchema = z.object({
  role: z.enum(POLL_ROLES),
  contact_id: z.number().int().nullable().optional(),
  name: z.string().trim().min(1, 'Name fehlt').max(200),
});
export const PollCreateSchema = z.object({
  dates: z.array(isoDate).min(1, 'Mindestens einen Tag vorschlagen').max(60),
  participants: z.array(PollParticipantInputSchema).min(1, 'Mindestens eine Person einladen').max(20),
  message: z.string().max(2000).nullable().optional(),
});
export type PollCreate = z.infer<typeof PollCreateSchema>;
export const PollPatchSchema = z.object({
  add_dates: z.array(isoDate).max(60).optional(),
  remove_dates: z.array(isoDate).max(60).optional(),
  add_participants: z.array(PollParticipantInputSchema).max(20).optional(),
  message: z.string().max(2000).nullable().optional(),
});
export type PollPatch = z.infer<typeof PollPatchSchema>;
export const PollChooseSchema = z.object({
  date: isoDate,
  /** Förster und Spendenpartner, die den Tag als „möglich“ markiert haben, gelten als bestätigt */
  confirm: z.boolean().default(true),
});
export const PollAnswerInputSchema = z.object({
  answers: z.record(isoDate, z.enum(POLL_ANSWERS)),
  comment: z.string().max(2000).nullable().optional(),
});
export type PollAnswerInput = z.infer<typeof PollAnswerInputSchema>;

export interface PollParticipantDto {
  id: number;
  role: PollRole;
  role_label: string;
  contact_id: number | null;
  name: string;
  email: string | null;
  token: string;
  responded_at: string | null;
  comment: string | null;
  answers: Record<string, PollAnswer>;
}
export interface PollDateDto {
  date: string;
  yes: number;
  maybe: number;
  no: number;
  /** alle mit „möglich“ oder „bedingt möglich“, keiner mit „nicht möglich“, alle haben geantwortet */
  fits_all: boolean;
  /** andere Aktionen an diesem Tag (Logistik: zwei Teams, Wege) */
  other_actions: { id: number; code: string; label: string; state_label: string | null; region: string | null }[];
}
export interface PollDto {
  id: number;
  action_id: number;
  status: 'open' | 'closed';
  message: string | null;
  chosen_date: string | null;
  closed_at: string | null;
  created_at: string;
  dates: PollDateDto[];
  participants: PollParticipantDto[];
}
/** Öffentliche Sicht für einen Teilnehmenden (ohne Antworten der anderen) */
export interface PublicPollDto {
  title: string;
  org_name: string;
  place: string | null;
  partner_name: string | null;
  participant: { name: string; role_label: string };
  message: string | null;
  status: 'open' | 'closed';
  chosen_date: string | null;
  dates: string[];
  answers: Record<string, PollAnswer>;
  comment: string | null;
  responded_at: string | null;
}

/** Wertung eines Tags: Tage, an denen alle können, zuerst; dann mehr „möglich“, weniger „bedingt möglich“ */
export function pollDateScore(d: { yes: number; maybe: number; no: number; fits_all: boolean }): number {
  return (d.fits_all ? 1000 : 0) + d.yes * 10 + d.maybe * 3 - d.no * 20;
}

/** Werktage (Mo bis Sa) eines Zeitraums, für „Zeitraum vorschlagen“; Sonntage nur auf Wunsch */
export function datesInRange(from: string, to: string, withSundays = false): string[] {
  const out: string[] = [];
  const start = new Date(from + 'T00:00:00Z');
  const end = new Date(to + 'T00:00:00Z');
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return out;
  for (let d = start; d <= end && out.length < 60; d = new Date(d.getTime() + 86400_000)) {
    if (!withSundays && d.getUTCDay() === 0) continue;
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

/** „Mo 03.11.“ */
export function fmtPollDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  const wd = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][d.getUTCDay()];
  return `${wd} ${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}
